import { MobileScoreCard } from '@/components/mobile/mobile-score-card'
import { QuickReplyChips } from '@/components/mobile/quick-reply-chips'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useEnsureChatId, useInfiniteMessages } from '@/hooks/use-chat'
import { useChatStreaming } from '@/hooks/use-chat-streaming'
import { useDocumentProcessingProgress } from '@/hooks/use-document-processing-progress'
import { useCurrentResume, useDocuments } from '@/hooks/use-documents'
import { useSSEEvent } from '@/hooks/use-event-stream'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import { externalJobsService } from '@/services/external-jobs.service'
import { useChatStore } from '@/stores/chat-store'
import type { ChatMessage } from '@/stores/chat-store'
import type { QuickAction } from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowDown, Loader, MessageCircle, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type InteractiveMetadataField,
  attachSupportQuickActionsWhenMissing,
  clearMessagesInteractiveMetadata,
  formatMessageTime,
  getMessageDisplayType,
  getVisibleQuickActions,
  getVisibleQuickReplies,
  groupMessagesByDate,
  isKnownQuickAction,
  loadDismissedInteractiveMessageIds,
  parseJobContext,
  parseMatchData,
  registerDismissedInteractiveMessageIds,
  retainOnlyLatestInteractiveMetadataForAllFields,
  stripDismissedInteractiveMetadata,
} from './chat-helpers'
import { ChatInput } from './chat-input'
import { getChatScrollAnchor, getPrependedScrollTop, getRestoredScrollTop } from './chat-scroll'
import { ConversationFeedback } from './conversation-feedback'
import { MessageFeedback } from './message-feedback'
import { InlineJobChip, InlineMatchCard, MESSAGE_RENDERERS } from './message-renderers'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const LOCAL_GREETINGS: Record<string, string> = {
  new_job:
    "Hello! I'm your Pelgo AI career coach. I'm here to help you navigate your job search.\n\n" +
    "We'll focus on organizing your job hunt, optimizing your resume for applicant tracking systems, " +
    'and targeting roles that align with your goals.\n\n' +
    "When you're ready, upload your resume to get started. What kind of roles are you looking for?",
  career_change:
    "Hello! I'm your Pelgo AI business transition coach. I'm excited to help you explore your entrepreneurial journey.\n\n" +
    "We'll focus on brainstorming business models, refining your value propositions, and structuring " +
    'your self-employment strategy.\n\n' +
    "Tell me about any business ideas you've been thinking about, or we can start brainstorming from scratch!",
  level_up:
    "Hello! I'm your Pelgo AI learning and reskilling coach. I'm here to help you build the skills for your next chapter.\n\n" +
    "We'll focus on analyzing your skill gaps, preparing for boot camps or academic programs, and designing a " +
    'personalized learning path.\n\n' +
    "What new skills or fields are you thinking about exploring? Let's discuss your reskilling goals!",
  exploring:
    "Hello! I'm your Pelgo AI transition coach. Think of this as a low-pressure space to explore what's next.\n\n" +
    "We'll focus on reflecting on your strengths and interests, and mapping out multiple potential career directions.\n\n" +
    "How are you holding up? Let me know what you'd like to reflect on, or we can just start discussing your key strengths.",
  offboarding:
    "Hello! I'm your Pelgo AI transition coach. I'm here to help you access your employment documents and navigate your benefits.\n\n" +
    "We'll focus on accessing your employment documents, tracking past benefits that can be helpful during your unemployment, and identifying ones that can carry over to your next role.\n\n" +
    'What documents or benefits are you looking to track or access today?',
}

interface ChatPanelProps {
  /** When true, renders side panels (resume/learning) in a provided slot instead of inline */
  className?: string
  variant?: 'coach' | 'agent'
}

function PelgoAgentAvatar() {
  return (
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-600">
      <img src="/logomark-white.png" alt="" className="h-4 w-4 object-contain" />
    </div>
  )
}

export function ChatPanel({ className, variant = 'coach' }: ChatPanelProps) {
  const isAgentVariant = variant === 'agent'
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { sendMessageStreaming, streamingIdRef, userScrolledUpRef } = useChatStreaming()
  const { currentResume } = useCurrentResume()

  const {
    chatId,
    messages,
    isSending,
    setMessages,
    userDocumentId,
    setPreviewPanelOpen,
    setResumeContent,
    setOriginalResumeContent,
    setUserDocumentId,
    scrollDistanceFromBottom: savedScrollDistanceFromBottom,
    scrollWasAtBottom: savedScrollWasAtBottom,
    setScrollAnchor,
    coachFeedbackToken,
    coachAttentionActive,
    clearCoachAttention,
  } = useChatStore()

  const acknowledgeCoachAttention = useCallback(() => {
    if (!coachAttentionActive) return
    // Ignore spurious clicks right as reach-out activates (same gesture / focus churn).
    if (Date.now() - coachAttentionArmedAtRef.current < 400) return
    clearCoachAttention()
  }, [coachAttentionActive, clearCoachAttention])

  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isMatchingJobs, setIsMatchingJobs] = useState(false)
  const [supportMode, setSupportMode] = useState(false)

  const { data: allDocuments } = useDocuments()
  const { processingItems } = useDocumentProcessingProgress(allDocuments)
  const [learningStatusText, setLearningStatusText] = useState<string | null>(null)

  // Listen for learning progress status via SSE
  useSSEEvent('learning_profile_status_changed', (event) => {
    const data = event.data as { status?: string; status_text?: string }
    if (data.status_text) {
      setLearningStatusText(data.status_text)
    } else if (data.status === 'completed' || data.status === 'failed') {
      setLearningStatusText(null)
    }
  })
  useSSEEvent('learning_topic_status_changed', (event) => {
    const data = event.data as { status?: string; status_text?: string }
    if (data.status_text) {
      setLearningStatusText(data.status_text)
    } else if (data.status === 'completed' || data.status === 'failed') {
      setLearningStatusText(null)
    }
  })
  useSSEEvent('learning_topic_generating', (event) => {
    const data = event.data as { status_text?: string }
    if (data.status_text) {
      setLearningStatusText(data.status_text)
    }
  })
  useSSEEvent('job_matching_status_changed', () => {
    if (!chatId) return
    void refetchMessages()
    queryClient.invalidateQueries({ queryKey: ['chats', chatId, 'messages', 'infinite'] })
  })

  const activeStatusText =
    processingItems.find((p) => p.statusText && p.status === 'processing')?.statusText ||
    learningStatusText

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const greetingInitiatedRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const shouldScrollToBottomRef = useRef(false)
  const previousScrollHeightRef = useRef<number>(0)
  const fetchingMoreRef = useRef(false)
  const shouldPreservePrependScrollRef = useRef(false)
  const savedScrollAnchorRef = useRef({
    distanceFromBottom: savedScrollDistanceFromBottom,
    wasAtBottom: savedScrollWasAtBottom,
  })
  const initialScrollRestoredRef = useRef(false)
  const sentPendingCoachMessageRef = useRef(false)
  const typingAutoFollowRef = useRef(false)
  const firstCoachBubbleAutoScrolledRef = useRef(false)
  const suppressScrollLockUntilRef = useRef(0)
  const coachAttentionArmedAtRef = useRef(0)
  const dismissedInteractiveMessageIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    shouldScrollToBottomRef.current = true
  }, [])

  useEffect(() => {
    if (isSending) {
      typingAutoFollowRef.current = true
      firstCoachBubbleAutoScrolledRef.current = false
      userScrolledUpRef.current = false
    }
  }, [isSending, userScrolledUpRef])

  useEffect(() => {
    if (!isSending) {
      typingAutoFollowRef.current = false
    }
  }, [isSending])

  useEffect(() => {
    if (!chatId || isSending || sentPendingCoachMessageRef.current) return

    const pendingRaw = sessionStorage.getItem('pending_coach_message')
    if (!pendingRaw) return

    try {
      const pending = JSON.parse(pendingRaw) as { message?: string; documentId?: number }
      if (pending.message) {
        sentPendingCoachMessageRef.current = true
        sessionStorage.removeItem('pending_coach_message')
        sendMessageStreaming(
          pending.message,
          typeof pending.documentId === 'number' ? [pending.documentId] : undefined,
          undefined,
          undefined,
        )
      }
    } catch {
      sessionStorage.removeItem('pending_coach_message')
    }
  }, [chatId, isSending, sendMessageStreaming])

  const { chatId: ensuredChatId, chatReady, chatsLoading } = useEnsureChatId()
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = useInfiniteMessages(chatId || '')

  const fetchedMessages = useMemo(() => {
    if (!infiniteData?.pages) return undefined
    const allPages = [...infiniteData.pages].reverse()
    return allPages.flat()
  }, [infiniteData])

  // Reset greeting flags when chatId changes
  useEffect(() => {
    dismissedInteractiveMessageIdsRef.current = chatId
      ? loadDismissedInteractiveMessageIds(chatId)
      : new Set()
    greetingInitiatedRef.current = false
    isInitialLoadRef.current = true
    initialScrollRestoredRef.current = false
    firstCoachBubbleAutoScrolledRef.current = false
    sentPendingCoachMessageRef.current = false
  }, [chatId])

  // Load document detail when document ID is set
  useEffect(() => {
    if (!userDocumentId) return
    setPreviewPanelOpen(true)

    const loadDocumentDetail = async () => {
      try {
        const detail = await documentService.getDocument(userDocumentId)
        if (detail.parsed_resume_data) {
          setOriginalResumeContent(detail.parsed_resume_data)
          if (!detail.draft_content_json) {
            setResumeContent(detail.parsed_resume_data)
          }
        }
        if (detail.draft_content_json) {
          setResumeContent(detail.draft_content_json)
        }
      } catch (error) {
        console.error('Failed to load document detail:', error)
      }
    }
    loadDocumentDetail()
  }, [userDocumentId, setPreviewPanelOpen, setOriginalResumeContent, setResumeContent])

  useEffect(() => {
    if (!userDocumentId || !chatId) return
    queryClient.invalidateQueries({ queryKey: ['chats', chatId, 'messages', 'infinite'] })
    void refetchMessages()
  }, [userDocumentId, chatId, queryClient, refetchMessages])

  const normalizeHydratedMessages = useCallback(
    (incoming: ChatMessage[]) => {
      const afterDismissed = stripDismissedInteractiveMetadata(
        incoming,
        dismissedInteractiveMessageIdsRef.current,
      )
      const afterSupport = attachSupportQuickActionsWhenMissing(afterDismissed)
      const { messages: afterLatest, supersededIds } =
        retainOnlyLatestInteractiveMetadataForAllFields(afterSupport)
      registerDismissedInteractiveMessageIds(
        chatId,
        supersededIds,
        dismissedInteractiveMessageIdsRef.current,
      )
      return afterLatest
    },
    [chatId],
  )

  // Load messages on initial page load only. After that, local state is the
  // source of truth - new messages come from streaming and SSE events, not refetches.
  useEffect(() => {
    if (fetchedMessages && !isSending) {
      const container = messagesContainerRef.current
      const prevScrollHeight = previousScrollHeightRef.current

      const nonEmptyMessages = fetchedMessages.filter((m) => m.content?.trim())
      setMessages((prev) => {
        const parseTime = (value: string | Date | undefined) => {
          const t = new Date(value ?? '').getTime()
          return Number.isFinite(t) ? t : 0
        }
        const existingIds = new Set(prev.map((m) => String(m.id)))
        const latestLocalTimestamp = prev.reduce((max, m) => {
          const ts = parseTime(m.created_at)
          return ts > max ? ts : max
        }, 0)
        const incomingNewerMessages = nonEmptyMessages.filter((m) => {
          const messageId = String(m.id)
          if (existingIds.has(messageId)) return false
          const serverTs = parseTime(m.created_at)
          return latestLocalTimestamp === 0 || serverTs >= latestLocalTimestamp
        })

        if (prev.length === 0) {
          return normalizeHydratedMessages(nonEmptyMessages)
        }
        if (incomingNewerMessages.length > 0) {
          return normalizeHydratedMessages([...prev, ...incomingNewerMessages])
        }
        return prev
      })

      if (isInitialLoadRef.current || shouldScrollToBottomRef.current) {
        const wasInitial = isInitialLoadRef.current
        isInitialLoadRef.current = false
        shouldScrollToBottomRef.current = false
        shouldPreservePrependScrollRef.current = false
        previousScrollHeightRef.current = 0
        window.setTimeout(() => {
          if (messagesContainerRef.current) {
            const currentContainer = messagesContainerRef.current
            const savedAnchor = savedScrollAnchorRef.current

            // A raw scrollTop becomes stale when older/newer messages change the
            // document height. Anchor to the bottom instead so the same messages
            // remain visible across coach-panel remounts.
            if (wasInitial && !savedAnchor.wasAtBottom) {
              currentContainer.scrollTop = getRestoredScrollTop(
                currentContainer.scrollHeight,
                currentContainer.clientHeight,
                savedAnchor,
              )
            } else {
              currentContainer.scrollTop = currentContainer.scrollHeight
            }
            initialScrollRestoredRef.current = true
          }
        }, 50)
      }

      if (container && shouldPreservePrependScrollRef.current && prevScrollHeight > 0) {
        const previousScrollTop = container.scrollTop
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight
            messagesContainerRef.current.scrollTop = getPrependedScrollTop(
              previousScrollTop,
              prevScrollHeight,
              newScrollHeight,
            )
            shouldPreservePrependScrollRef.current = false
            previousScrollHeightRef.current = 0
          }
        })
      }
    }
  }, [fetchedMessages, isSending, normalizeHydratedMessages, setMessages])

  // Reset fetch guard when fetch completes
  useEffect(() => {
    if (!isFetchingNextPage) {
      fetchingMoreRef.current = false
    }
  }, [isFetchingNextPage])

  // Auto-load more messages when at top
  useEffect(() => {
    if (
      isInitialLoadRef.current ||
      !initialScrollRestoredRef.current ||
      !hasNextPage ||
      isFetchingNextPage ||
      fetchingMoreRef.current ||
      !messagesContainerRef.current
    )
      return

    const container = messagesContainerRef.current
    const isAtTop = container.scrollTop < 10
    const contentFitsViewport = container.scrollHeight <= container.clientHeight

    if (isAtTop || contentFitsViewport) {
      fetchingMoreRef.current = true
      shouldPreservePrependScrollRef.current = true
      previousScrollHeightRef.current = container.scrollHeight
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // AI-initiated greeting logic
  useEffect(() => {
    if (!chatId || greetingInitiatedRef.current || isSending) return
    if (fetchedMessages === undefined) return

    // Determine whether this user has an existing conversation history.
    // Use localStorage as a secondary signal so a transient empty fetch
    // (network glitch) cannot trick us into showing the first-time greeting
    // to a returning user.
    const hasConversationHistoryKey = `hasConversation_${chatId}`
    const hadPriorConversation = localStorage.getItem(hasConversationHistoryKey) === '1'

    if (fetchedMessages.length > 0) {
      // Persist the fact that this chat has messages so future sessions
      // never accidentally treat the user as brand-new.
      localStorage.setItem(hasConversationHistoryKey, '1')
    }

    const isReturningUser = fetchedMessages.length > 0 || hadPriorConversation

    const shouldGreet = () => {
      if (!isReturningUser) return true

      const lastMessage = fetchedMessages[fetchedMessages.length - 1]
      if (!lastMessage?.created_at) return false

      const lastMessageTime = new Date(lastMessage.created_at).getTime()
      const now = Date.now()
      const hoursSinceLastMessage = (now - lastMessageTime) / (1000 * 60 * 60)

      if (hoursSinceLastMessage >= 12) {
        const lastGreetingKey = `lastGreeting_${chatId}`
        const lastGreetingTime = localStorage.getItem(lastGreetingKey)

        if (!lastGreetingTime) return true

        const hoursSinceLastGreeting = (now - Number.parseInt(lastGreetingTime)) / (1000 * 60 * 60)
        return hoursSinceLastGreeting >= 12
      }

      return false
    }

    if (shouldGreet()) {
      greetingInitiatedRef.current = true

      const lastGreetingKey = `lastGreeting_${chatId}`
      localStorage.setItem(lastGreetingKey, Date.now().toString())

      const firstName = user?.first_name || user?.full_name?.split(' ')[0]
      const welcomeBack = firstName ? `Welcome back, ${firstName}!` : 'Welcome back!'

      const welcomeBackGreetings = [
        `${welcomeBack} How are you doing? Is there anything I can help you with today?`,
        `${welcomeBack} How have things been going? What would you like to talk about?`,
        `${welcomeBack} I hope you're doing well. What's on your mind today?`,
        `${welcomeBack} How are things progressing? Is there anything new you'd like to discuss?`,
      ]

      const selectRandom = (arr: string[]): string => {
        return arr[Math.floor(Math.random() * arr.length)] as string
      }

      const getGreetingAndSave = async () => {
        let greetingContent = ''

        if (isReturningUser) {
          greetingContent = selectRandom(welcomeBackGreetings)
        } else {
          // Fetch custom greeting from backend API
          try {
            const token = localStorage.getItem('auth_token')
            const response = await fetch(`${API_URL}/api/v1/chat/greeting`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.greeting) {
                greetingContent = data.greeting
              }
            }
          } catch (error) {
            console.error('[Greeting] Failed to fetch custom greeting from backend:', error)
          }

          // Local fallback using localStorage key and LOCAL_GREETINGS mapping
          if (!greetingContent) {
            const cachedGoal = localStorage.getItem('selected_learning_goal')
            const localGreeting = cachedGoal ? LOCAL_GREETINGS[cachedGoal] : null
            if (localGreeting) {
              greetingContent = localGreeting
            } else {
              greetingContent = `${firstName ? `Hello ${firstName},` : 'Hello,'} I'm Pelgo AI.\nI'll be your coach as we navigate this transition together.\n\nHow are you holding up? I know things might feel uncertain right now, but I'm here to help you turn this into a fresh start.\n\nWhen you're ready to take that first step, go ahead and upload your resume. Even if it's outdated, it gives us a foundation to build on. I'll help you refine the details and get you ready for your next great role.\n\nOr if you don't have a resume yet, no worries, just let me know - we can help you with that too.`
            }
          }
        }

        const quickActions: QuickAction[] = []

        const greetingMessage: ChatMessage = {
          id: `greeting-${Date.now()}`,
          role: 'assistant',
          content: greetingContent,
          created_at: new Date().toISOString(),
          ...(quickActions.length > 0 && { msg_metadata: { quick_actions: quickActions } }),
        }

        setMessages((prev) => [...prev, greetingMessage])

        try {
          const token = localStorage.getItem('auth_token')
          const response = await fetch(`${API_URL}/api/v1/chat/${chatId}/messages/greeting`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              role: 'assistant',
              content: greetingContent,
              channel: 'web',
            }),
          })
          if (!response.ok) {
            console.error('[Greeting] Server rejected greeting save:', response.status)
            return
          }
          const saved = await response.json()
          if (saved?.id) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === greetingMessage.id
                  ? { ...m, id: saved.id, created_at: saved.created_at || m.created_at }
                  : m,
              ),
            )
          }
        } catch (error) {
          console.error('[Greeting] Failed to save greeting to database:', error)
        }
      }

      void getGreetingAndSave()

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [chatId, fetchedMessages, isSending, user?.first_name, user?.full_name, setMessages])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current
    if (!container) return

    if (behavior === 'smooth') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      return
    }

    container.scrollTop = container.scrollHeight
  }, [])

  // Auto-scroll once when the first assistant content bubble appears.
  // Locate the streaming message from shared state rather than the local
  // streamingIdRef: when a message is sent from another surface (e.g. the
  // Opportunities "please reach out to us" button), this panel's own
  // streamingIdRef is never set, but the message still lands in `messages`.
  useEffect(() => {
    if (!typingAutoFollowRef.current || firstCoachBubbleAutoScrolledRef.current) return

    const streamingMessage = messages.find((msg) => msg.role === 'assistant' && msg.isStreaming)
    if (!streamingMessage) return

    if (!streamingMessage.content?.trim()) return

    scrollToBottom('auto')
    firstCoachBubbleAutoScrolledRef.current = true
    typingAutoFollowRef.current = false
  }, [messages, scrollToBottom])

  // Auto-scroll during streaming
  useEffect(() => {
    const streamingMessage = messages.find((msg) => msg.role === 'assistant' && msg.isStreaming)
    const typingPlaceholderActive = Boolean(streamingMessage && !streamingMessage.content?.trim())

    if (typingAutoFollowRef.current && typingPlaceholderActive && !userScrolledUpRef.current) {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      }
    }
  }, [messages, userScrolledUpRef])

  // External coach triggers (e.g. Opportunities): re-arm follow and gently scroll
  // only when the user is reading older messages — avoids a jarring full-panel jump.
  useEffect(() => {
    if (coachFeedbackToken === 0) return
    coachAttentionArmedAtRef.current = Date.now()

    userScrolledUpRef.current = false
    typingAutoFollowRef.current = true
    firstCoachBubbleAutoScrolledRef.current = false
    suppressScrollLockUntilRef.current = Date.now() + 2500

    const timer = window.setTimeout(() => {
      const container = messagesContainerRef.current
      if (!container) return
      const { scrollTop, scrollHeight, clientHeight } = container
      const nearBottom = scrollHeight - scrollTop - clientHeight < 120
      if (!nearBottom) {
        scrollToBottom('smooth')
      }
    }, 600)

    return () => window.clearTimeout(timer)
  }, [coachFeedbackToken, scrollToBottom, userScrolledUpRef])

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const nearBottom = scrollHeight - scrollTop - clientHeight < 100
      const nearTop = scrollTop < 100

      // Persist a bottom-relative anchor. This remains valid when pagination
      // prepends older messages or new messages arrive while the panel is hidden.
      const scrollAnchor = getChatScrollAnchor({ scrollTop, scrollHeight, clientHeight })
      setScrollAnchor(scrollAnchor.distanceFromBottom, scrollAnchor.wasAtBottom)

      const scrollLockSuppressed = Date.now() < suppressScrollLockUntilRef.current
      if (
        !scrollLockSuppressed &&
        (streamingIdRef.current !== undefined || isSending) &&
        !nearBottom
      ) {
        userScrolledUpRef.current = true
      }

      setShowScrollButton(!nearBottom && messages.length > 0)

      if (nearTop && hasNextPage && !isFetchingNextPage && !fetchingMoreRef.current) {
        fetchingMoreRef.current = true
        shouldPreservePrependScrollRef.current = true
        previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight
        fetchNextPage()
      }
    }
  }

  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages])

  const dismissInteractiveMetadata = useCallback(
    (messageId: string | number, fields: InteractiveMetadataField[]) => {
      const normalizedId = String(messageId)
      if (dismissedInteractiveMessageIdsRef.current.has(normalizedId)) return false

      registerDismissedInteractiveMessageIds(
        chatId,
        [normalizedId],
        dismissedInteractiveMessageIdsRef.current,
      )
      setMessages((prev) => clearMessagesInteractiveMetadata(prev, messageId, fields))
      return true
    },
    [chatId, setMessages],
  )

  const handleQuickActionClick = useCallback(
    (msg: ChatMessage, action: QuickAction) => {
      if (!getVisibleQuickActions(msg, dismissedInteractiveMessageIdsRef.current).length) return
      if (!dismissInteractiveMetadata(msg.id, ['quick_actions'])) return

      if (!isKnownQuickAction(action)) {
        console.warn('[Chat] Ignoring unknown quick action:', action.action)
        return
      }

      if (action.action === 'navigate') {
        navigate(action.target)
        return
      }
      if (action.action === 'support_request') {
        setSupportMode(true)
        return
      }
      sendMessageStreaming(action.target)
    },
    [dismissInteractiveMetadata, navigate, sendMessageStreaming],
  )

  const handleQuickReplySelect = useCallback(
    (msg: ChatMessage, option: string) => {
      if (!getVisibleQuickReplies(msg, dismissedInteractiveMessageIdsRef.current).length) return
      if (!dismissInteractiveMetadata(msg.id, ['quick_replies'])) return

      if (option === 'View matched jobs') {
        const matchedResumeId = msg.msg_metadata?.quick_replies_document_id
        if (typeof matchedResumeId === 'number' && matchedResumeId > 0) {
          navigate(`/opportunities?resume_id=${matchedResumeId}`)
        } else {
          navigate('/opportunities')
        }
        return
      }
      sendMessageStreaming(option)
    },
    [dismissInteractiveMetadata, navigate, sendMessageStreaming],
  )

  const handleMatchWithJobs = useCallback(async () => {
    if (isMatchingJobs) return
    const resumeId = userDocumentId ?? currentResume?.id
    if (!resumeId) {
      navigate('/resume')
      return
    }

    setIsMatchingJobs(true)
    try {
      const numericChatId = chatId ? Number(chatId) : null
      await externalJobsService.triggerMatchResume(resumeId, numericChatId, isMobile)
    } catch (error) {
      console.error('Failed to trigger job matching from chat:', error)
    } finally {
      setIsMatchingJobs(false)
    }
  }, [chatId, currentResume, isMatchingJobs, isMobile, navigate, userDocumentId])

  if (chatsLoading || !chatReady || !ensuredChatId) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <div className="text-center space-y-4">
          <Loader className="h-6 w-6 animate-spin mx-auto text-blue-600" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {!isAgentVariant && (
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-3 border-b shrink-0 transition-colors',
            coachAttentionActive
              ? 'animate-pulse cursor-pointer bg-sky-50 border-sky-200'
              : 'bg-background',
          )}
          onClick={acknowledgeCoachAttention}
          role={coachAttentionActive ? 'button' : undefined}
          tabIndex={coachAttentionActive ? 0 : undefined}
          onKeyDown={
            coachAttentionActive
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    acknowledgeCoachAttention()
                  }
                }
              : undefined
          }
          aria-label={
            coachAttentionActive ? 'New coach activity — click to acknowledge' : undefined
          }
        >
          <Sparkles className="h-4 w-4 text-sky-500 shrink-0" />
          <span
            className={cn(
              'text-sm font-semibold tracking-tight',
              coachAttentionActive && 'text-sky-700',
            )}
          >
            Pelgo coach
          </span>
          {coachAttentionActive && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-sky-400 animate-pulse" aria-hidden />
          )}
        </div>
      )}

      {/* Messages container */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        onMouseDown={acknowledgeCoachAttention}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4"
      >
        {messages.length > 0 ? (
          <div className="space-y-4">
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-3">
                <Loader className="h-4 w-4 animate-spin text-blue-600" />
                <span className="ml-2 text-xs text-muted-foreground">
                  Loading older messages...
                </span>
              </div>
            )}
            {hasNextPage && !isFetchingNextPage && (
              <button
                type="button"
                onClick={() => {
                  if (fetchingMoreRef.current) return
                  fetchingMoreRef.current = true
                  shouldPreservePrependScrollRef.current = true
                  if (messagesContainerRef.current) {
                    previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight
                  }
                  fetchNextPage()
                }}
                className="flex items-center justify-center py-2 w-full hover:bg-muted/50 rounded transition-colors"
              >
                <span className="text-xs text-muted-foreground hover:text-foreground">
                  Click or scroll up for older messages
                </span>
              </button>
            )}
            {Object.entries(messageGroups).map(([dateLabel, msgs]) => (
              <div key={dateLabel}>
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                  <span className="text-xs font-medium text-black dark:text-white shrink-0">
                    {dateLabel}
                  </span>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                </div>

                <div className="space-y-4">
                  {msgs.map((msg) => {
                    const showLegacyBubble =
                      msg.role === 'user' ||
                      msg.error ||
                      msg.msg_metadata?.type === 'resume_upload' ||
                      (msg.isStreaming && !msg.content)
                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          'flex w-full',
                          isAgentVariant
                            ? msg.role === 'user'
                              ? 'justify-end gap-2'
                              : 'justify-start gap-2'
                            : 'flex-col',
                          !isAgentVariant && (msg.role === 'user' ? 'items-end' : 'items-start'),
                        )}
                      >
                        {isAgentVariant && msg.role === 'assistant' && <PelgoAgentAvatar />}
                        <div
                          className={cn(
                            'min-w-0',
                            isAgentVariant ? 'flex max-w-[85%] flex-col' : 'contents',
                            !isAgentVariant && 'contents',
                          )}
                        >
                          <div
                            className={cn(
                              'break-words overflow-x-hidden',
                              showLegacyBubble && 'rounded-2xl px-4 py-2.5 max-w-[90%] shadow-sm',
                              showLegacyBubble &&
                                (msg.role === 'user'
                                  ? 'bg-sky-100 text-black border border-sky-200 rounded-br-sm'
                                  : msg.error
                                    ? 'bg-red-50 border border-red-200 rounded-bl-sm'
                                    : isAgentVariant
                                      ? 'bg-slate-100 border border-slate-200 rounded-bl-sm text-black'
                                      : 'bg-gradient-to-br from-muted to-muted/70 rounded-bl-sm text-black dark:text-white'),
                            )}
                          >
                            {(() => {
                              const MessageRenderer = MESSAGE_RENDERERS[getMessageDisplayType(msg)]
                              return (
                                <MessageRenderer
                                  msg={msg}
                                  onRevisionGenerated={(revision) => {
                                    setMessages((prev) =>
                                      prev.map((m) => {
                                        if (m.id === msg.id) {
                                          const { resumePreview: _, ...rest } = m
                                          return { ...rest, revision }
                                        }
                                        return m
                                      }),
                                    )
                                  }}
                                />
                              )
                            })()}
                          </div>
                          {(() => {
                            const jobContext =
                              msg.msg_metadata?.job_context ||
                              msg.jobContext ||
                              parseJobContext(msg.content)
                            return jobContext ? <InlineJobChip jobContext={jobContext} /> : null
                          })()}
                          {(() => {
                            const matchData =
                              msg.msg_metadata?.match_data ||
                              msg.matchData ||
                              parseMatchData(msg.content)
                            return matchData ? <InlineMatchCard matchData={matchData} /> : null
                          })()}
                          {msg.role === 'assistant' && !msg.error && <MessageFeedback msg={msg} />}
                          {(() => {
                            const quickActions = getVisibleQuickActions(
                              msg,
                              dismissedInteractiveMessageIdsRef.current,
                            )
                            if (quickActions.length === 0) return null

                            return (
                              <div className="flex flex-wrap gap-2 mt-2 px-1">
                                {quickActions.map((action) => (
                                  <Button
                                    key={`${action.label}-${action.action}-${action.target}`}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs rounded-full px-4"
                                    onClick={() => handleQuickActionClick(msg, action)}
                                  >
                                    {action.label}
                                  </Button>
                                ))}
                              </div>
                            )
                          })()}
                          {msg.msg_metadata?.quality_score &&
                            (!isMobile || msg.role === 'assistant') && (
                              <div className="mt-2 max-w-[90%]">
                                <MobileScoreCard
                                  score={msg.msg_metadata.quality_score.overall}
                                  breakdown={
                                    isMobile
                                      ? [
                                          {
                                            dimension: 'Skills',
                                            score: msg.msg_metadata.quality_score.skills_coverage,
                                          },
                                          {
                                            dimension: 'Experience',
                                            score:
                                              msg.msg_metadata.quality_score.experience_clarity,
                                          },
                                          {
                                            dimension: 'Formatting',
                                            score: msg.msg_metadata.quality_score.formatting,
                                          },
                                        ]
                                      : [
                                          {
                                            dimension: 'Skills Coverage',
                                            score: msg.msg_metadata.quality_score.skills_coverage,
                                          },
                                          {
                                            dimension: 'Experience Clarity',
                                            score:
                                              msg.msg_metadata.quality_score.experience_clarity,
                                          },
                                          {
                                            dimension: 'Formatting',
                                            score: msg.msg_metadata.quality_score.formatting,
                                          },
                                        ]
                                  }
                                  onImprove={() =>
                                    isMobile
                                      ? sendMessageStreaming(
                                          "I'd like to improve my resume. Can you help me with specific suggestions?",
                                        )
                                      : sendMessageStreaming('Improve with Coach')
                                  }
                                  onMatchJobs={() => {
                                    if (isMobile) {
                                      navigate('/opportunities')
                                      return
                                    }
                                    void handleMatchWithJobs()
                                  }}
                                />
                              </div>
                            )}
                          {(() => {
                            const quickReplies = getVisibleQuickReplies(
                              msg,
                              dismissedInteractiveMessageIdsRef.current,
                            )
                            if (quickReplies.length === 0) return null

                            return (
                              <div className="mt-2 max-w-[90%]">
                                <QuickReplyChips
                                  options={quickReplies}
                                  onSelect={(option) => handleQuickReplySelect(msg, option)}
                                />
                              </div>
                            )
                          })()}
                          <span className="text-xs text-muted-foreground mt-1 px-2">
                            {formatMessageTime(msg.created_at || new Date())}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
            {messages.length >= 6 && <ConversationFeedback chatId={ensuredChatId} />}
            {activeStatusText && (
              <div className="flex items-center gap-2 py-2 px-1">
                <Loader className="h-3 w-3 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground italic">{activeStatusText}</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : isAgentVariant ? (
          <div className="flex h-full items-start px-1 pt-2">
            <div className="flex max-w-[90%] items-start gap-2">
              <PelgoAgentAvatar />
              <div className="rounded-2xl rounded-bl-sm border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-black shadow-sm">
                Ask me to find roles that match your resume — e.g. &apos;find software engineer
                jobs&apos;
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <div className="text-center space-y-3 max-w-[250px]">
              <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-blue-500 flex items-center justify-center mb-3 shadow-lg shadow-blue-500/30">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-base font-medium">Start the Conversation</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Share what's on your mind -- job search, interview prep, or just processing your
                feelings.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          type="button"
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-20 right-4 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all z-10"
          aria-label="Scroll to bottom"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      )}

      {/* Input area */}
      <div onMouseDown={isAgentVariant ? undefined : acknowledgeCoachAttention}>
        <ChatInput
          onSendMessage={sendMessageStreaming}
          onMatchComplete={async (userMsg, assistantMsg) => {
            setMessages((prev) => [...prev, userMsg, assistantMsg])

            if (chatId) {
              const token = localStorage.getItem('auth_token')
              try {
                await fetch(`${API_URL}/api/v1/chat/${chatId}/messages/greeting`, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    role: 'user',
                    content: userMsg.content,
                    channel: 'web',
                    msg_metadata: userMsg.msg_metadata,
                  }),
                })
                await fetch(`${API_URL}/api/v1/chat/${chatId}/messages/greeting`, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    role: 'assistant',
                    content: assistantMsg.content,
                    channel: 'web',
                    msg_metadata: assistantMsg.msg_metadata,
                  }),
                })
              } catch (error) {
                console.error('Failed to persist match messages:', error)
              }
            }
          }}
          onDocumentReady={(documentId) => {
            setUserDocumentId(documentId)
            if (chatId) {
              queryClient.invalidateQueries({ queryKey: ['chats', chatId, 'messages', 'infinite'] })
            }
            void refetchMessages()
          }}
          isSending={isSending}
          supportMode={supportMode}
          onSupportModeChange={setSupportMode}
          {...(user?.email ? { userEmail: user.email } : {})}
          {...(user?.full_name || user?.first_name
            ? { userName: user.full_name || user.first_name || '' }
            : {})}
          onSupportSubmitted={(supportMessage) => {
            const confirmationMsg: ChatMessage = {
              id: `support-${Date.now()}`,
              role: 'assistant',
              content: `Thanks for reaching out. All messages are answered within hours. We'll follow up at ${user?.email || 'your email'}.`,
              created_at: new Date().toISOString(),
            }
            setMessages((prev) => [
              ...prev,
              {
                id: `support-user-${Date.now()}`,
                role: 'user',
                content: supportMessage,
                created_at: new Date().toISOString(),
              },
              confirmationMsg,
            ])
          }}
          compact
          variant={variant}
        />
        {!isAgentVariant && (
          <p className="text-[10px] text-center text-muted-foreground pb-2">
            Pelgo can make mistakes. Check important info.
          </p>
        )}
      </div>
    </div>
  )
}
